import React, { useState, useMemo, useEffect, useContext } from 'react'
import { Link, useParams } from 'react-router-dom'
import SeniorPackageInstructions from '../../senior-package-instructions'
import { useSeniorOrderPackage } from './use-senior-order-package'
import DroppableArea from '../../droppable-area'
import { pluralize } from '../../../utils/functions'
import Typography from '../../Typography'
import PackageBuilderHelper from '../../package-builder-helper'
import classes from './senior-portraits-review.module.sass'
import { Package } from '../../../models'
import Button from '../../Button'
import useImageTypesFromOption from '../package-builder/use-image-types-from-option'
import useAfterReviewRoute from '../../../hooks/useAfterReviewRoute';
import { CustomerContext } from '../../../contexts/CustomerContext';

const SHEETS = 'SHEETS'
const ADDON_SHEETS = 'ADDON_SHEETS'

const SeniorPortraitsReview = ({ isSenior = true }) => {
  const { orderPackageId } = useParams()
  const orderPackage = useSeniorOrderPackage(orderPackageId, isSenior)
  const [step, setStep] = useState(0)
  const [isEditing, setIsEditing] = useState(false)
  const [editType, setEditType] = useState('')
  const [prefillSheets, setPrefillSheets] = useState([])
  const [backgrounds, setBackgrounds] = useState([])
  const imageTypes = useImageTypesFromOption(orderPackage && orderPackage.optionId, false)
  const [selectedPackage, setSelectedPackage] = useState()
  const { customer } = useContext(CustomerContext);
  // Where Continue goes. Shared with the sport team-picture page, which skips
  // this screen and must land in the same place -- see hooks/useAfterReviewRoute.
  const { route: nextRoute } = useAfterReviewRoute({
    packageId: orderPackage && orderPackage.packageId,
    schoolId: customer.currentSchool.id,
    studentId: orderPackage && orderPackage.studentId,
    cartId: customer.cart && customer.cart.id,
    isSenior,
  });

  useEffect(() => {
    if (!orderPackage) return

    Package
      .includes({ backgroundSet: 'backgrounds' })
      .find(orderPackage.apiOption.packageId)
      .then((res) => {
        setSelectedPackage(res.data)
        setBackgrounds(res.data.backgroundSet.backgrounds)
      })
  }, [orderPackage])

  const sheetImageTypes = useMemo(() => {
    if (!orderPackage) return []

    return [...new Set(orderPackage.sheets.map((sheet) => sheet.imageType).filter((imageType) => !!imageType))]
  }, [orderPackage])

  const addons = useMemo(() => {
    if (!orderPackage) return []

    return [...new Set(orderPackage.addonSheets.map((addonSheet) => addonSheet.addon))]
  })

  const studentImages = useMemo(() => {
    if (!orderPackage) return []

    const foregrounds = [...new Set(orderPackage.sheets.map(({ pose, seniorImage }) => (pose || seniorImage)))]

    if (foregrounds.some((foreground) => foreground === undefined)) return []

    return foregrounds
  }, [orderPackage])

  if (!orderPackage) return null

  const { apiOption, sheets, addonSheets } = orderPackage

  const startEditing = (nextEditType) => {
    setEditType(nextEditType)
    setIsEditing(true)
  }

  const editSheets = (id) => {
    setPrefillSheets(sheets.filter((sheet) => sheet.imageType.id === id))
    startEditing(SHEETS)
  }

  const editAddonSheets = (id) => {
    setPrefillSheets(addonSheets.filter((addonSheet) => addonSheet.addon.id === id))
    startEditing(ADDON_SHEETS)
  }

  const key = isSenior ? 'seniorImage' : 'pose'

  const buttonText = () => {
    if (!isSenior) return 'Continue'

    if (orderPackage.price > 0) return 'Continue to Payment'
    return 'Continue to Checkout'
  }

  let toEditSheets = prefillSheets.map(({ imageType, addon }) => {
    if (editType === SHEETS) return imageType

    return { ...addon, count: addon.imageCount, addonId: addon.id }
  })

  const isEditingDigital = toEditSheets.map(({ name }) => name && name.toLowerCase()).find((name) => name && name.includes('digital'))
  if (!isEditingDigital) {
    toEditSheets = [...new Set(toEditSheets)]
  }

  return (
    <div>
      {apiOption.poses && (
        <SeniorPackageInstructions
          title={`${apiOption.name.trim()} (Up to ${pluralize('Pose', apiOption.poses)})`}
        />
      )}
      {!isEditing && step === 0 && (
        <div className={classes.content}>
          {sheetImageTypes.map(({ id, name }) => (
            <div key={id}>
              <div className={classes.header}>
                <Typography weight="bold">{name}</Typography>
                <Typography
                  align="center"
                  tag="span"
                  weight="bold"
                  classes={classes.editLink}
                  onClick={() => editSheets(id)}
                >
                  Edit
                </Typography>
              </div>
              <div className={classes.sheetsContainer}>
                {orderPackage
                  .sheets
                  .filter((sheet) => sheet.imageTypeId && sheet.imageTypeId.toString() === id)
                  .map((sheet) => (
                    <DroppableArea
                      checkForeground={sheet.seniorImageId || sheet.poseId}
                      placeholder={selectedPackage && selectedPackage.placeholderImage()}
                      poses={!isSenior}
                      key={sheet.id}
                      prefillSheet={sheet}
                      imageType={sheet.imageType}
                    />
                  ))}
              </div>
            </div>
          ))}
          {isSenior && (
            <>
              <div className={classes.header}>
                <Typography weight="bold">Yearbook Pose</Typography>
              </div>
              <div className={classes.sheetsContainer}>
                <DroppableArea
                  prefillSheet={{ [key]: orderPackage[key] }}
                  imageType={{ classname: '', id: -1 }}
                />
              </div>
            </>
          )}
          {imageTypes && imageTypes.map(({ name }) => name).includes('Digital Files') && (
            <Typography weight="bold">Digital Files Included</Typography>
          )}
          {addons.length > 0 && (
            <>
              <Typography weight="bold" classes={classes.addonHeader}>Addons</Typography>
              {addons.map(({ id, name, imageCount }) => (
                <div key={id}>
                  <div className={classes.header}>
                    <Typography weight="bold">{name}</Typography>
                    <Typography
                      tag="span"
                      weight="bold"
                      classes={classes.editLink}
                      onClick={() => editAddonSheets(id)}
                    >
                      Edit
                    </Typography>
                  </div>
                  <div className={classes.sheetsContainer}>
                    {orderPackage
                      .addonSheets
                      .filter((addonSheet) => addonSheet.addonId.toString() === id)
                      .map((addonSheet) => (
                        <DroppableArea
                          key={addonSheet.id}
                          checkForeground={addonSheet.seniorImageId || addonSheet.poseId}
                          placeholder={selectedPackage && selectedPackage.placeholderImage()}
                          addons
                          poses={!isSenior}
                          prefillSheet={addonSheet}
                          imageType={{ name, count: imageCount, addonId: id }}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {!isEditing && step === 1 && (
        <Typography align="center">Updating your package...</Typography>
      )}
      {isEditing && (
        <PackageBuilderHelper
          placeholder={selectedPackage.placeholderImage()}
          checkForeground={studentImages.length > 0}
          poses={!isSenior}
          checkMaxPoses={false}
          addons={editType === ADDON_SHEETS}
          studentImages={studentImages}
          backgrounds={backgrounds}
          selectedOption={orderPackage.apiOption}
          selectedPackage={orderPackage.apiOption.package}
          sheetList={toEditSheets}
          onComplete={() => {
            setStep(1)
            setIsEditing(false)
            orderPackage.save({ with: ['sheets', 'addonSheets'] }).then(() => setStep(0))
          }}
          maxPoses={10_000}
          prefilledSheets={prefillSheets}
          onBack={() => setIsEditing(false)}
          backText="Exit Editing"
          forwardText="Finish Editing"
        />
      )}
      {!isEditing && (
        <Link to={nextRoute} className={classes.continueBtn}>
          <Button rounded variant="primary">
            {buttonText()}
          </Button>
        </Link>
      )}
    </div>
  )
}

export default SeniorPortraitsReview
